import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/client';
import { parseWorkJson, EXAMPLE_JSON, aiPrompt } from '../utils/workJson';
import '../components/sets/FertRawMaterialPopup.css';
import './WorkReportPopup.css';

function calcHours(startTime, endTime, breakMin) {
    if (!startTime || !endTime) return null;
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const totalMin = (eh * 60 + em) - (sh * 60 + sm) - (parseInt(breakMin) || 0);
    if (totalMin <= 0) return null;
    return (totalMin / 60).toFixed(2);
}

function getWeekDates(anchor) {
    const d = new Date(anchor + 'T12:00:00');
    const dow = d.getDay();
    const offset = dow === 0 ? -6 : 1 - dow;
    const mon = new Date(d);
    mon.setDate(d.getDate() + offset);
    return Array.from({ length: 7 }, (_, i) => {
        const day = new Date(mon);
        day.setDate(mon.getDate() + i);
        return day.toISOString().split('T')[0];
    });
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function fmtDate(iso) {
    if (!iso) return '';
    const [, m, d] = iso.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[parseInt(m) - 1]} ${parseInt(d)}`;
}

function makeWeekRows(anchor, defaults = {}) {
    const today = new Date().toISOString().split('T')[0];
    return getWeekDates(anchor).map((date, i) => ({
        date,
        dayName: DAY_NAMES[i],
        enabled: i < 5 && date <= today,
        isFuture: date > today,
        shift:        defaults.shift        || '1',
        startTime:    defaults.startTime    || '08:00',
        endTime:      defaults.endTime      || '16:00',
        breakMinutes: defaults.breakMinutes || '0',
    }));
}

const WorkReportPopup = ({ isOpen, onClose, onSaved, editData = null, prefillDate = null }) => {
    const [employees, setEmployees] = useState([]);
    const [mode, setMode] = useState('single');

    // Single-day state
    const [form, setForm] = useState({
        employeeID: '', workday: '', shift: '1',
        startTime: '08:00', endTime: '16:00', breakMinutes: '0',
    });

    // Weekly state
    const [weekEmpID,  setWeekEmpID]  = useState('');
    const [weekAnchor, setWeekAnchor] = useState('');
    const [weekRows,   setWeekRows]   = useState([]);
    const [quickFill,  setQuickFill]  = useState({
        shift: '1', startTime: '08:00', endTime: '16:00', breakMinutes: '0',
    });

    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');
    const [shifts, setShifts] = useState([{ id: 1, name: 'Morning', is_active: 1 }]);
    // "Paste JSON" mode: records prepared by an AI from chat messages (utils/workJson.js)
    const [jsonText, setJsonText] = useState('');
    const [copied, setCopied] = useState('');
    const jsonParsed = useMemo(() => parseWorkJson(jsonText, employees, shifts), [jsonText, employees, shifts]); // before the early return: hooks order

    useEffect(() => {
        if (!isOpen) return;
        api.get('/api/work-reports/employees')
            .then(r => setEmployees(r.data || [])).catch(() => {});
        // shifts dictionary (production_shifts): workperformance.shift = its id, 1 = Morning
        api.get('/api/work-reports/shifts')
            .then(r => setShifts((r.data || []).filter(s => s.is_active))).catch(() => {});
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        setError('');
        setMode('single');
        if (editData) {
            setForm({
                employeeID:   String(editData.employeeID),
                workday:      editData.workday,
                shift:        String(editData.shift || 1),
                startTime:    editData.startTime   ? editData.startTime.slice(0,5)   : '08:00',
                endTime:      editData.endTime     ? editData.endTime.slice(0,5)     : '16:00',
                breakMinutes: String(editData.breakMinutes ?? 0),
            });
        } else {
            const anchor = prefillDate || new Date().toISOString().split('T')[0];
            setForm({
                employeeID: '', workday: prefillDate || '', shift: '1',
                startTime: '08:00', endTime: '16:00', breakMinutes: '0',
            });
            setWeekEmpID('');
            setWeekAnchor(anchor);
            setWeekRows(makeWeekRows(anchor));
        }
    }, [isOpen, editData, prefillDate]);

    // Switch to week mode: keep employee if already chosen
    const switchToWeek = () => {
        if (form.employeeID) setWeekEmpID(form.employeeID);
        const anchor = form.workday || prefillDate || new Date().toISOString().split('T')[0];
        setWeekAnchor(anchor);
        setWeekRows(makeWeekRows(anchor, quickFill));
        setMode('week');
    };

    const handleWeekAnchorChange = (e) => {
        const val = e.target.value;
        setWeekAnchor(val);
        if (val) setWeekRows(makeWeekRows(val, quickFill));
    };

    const applyQuickFill = () => {
        setWeekRows(rows => rows.map(r => r.enabled ? { ...r, ...quickFill } : r));
    };

    const updateWeekRow = (idx, field, value) => {
        setWeekRows(rows => rows.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    };

    const hours = useMemo(
        () => calcHours(form.startTime, form.endTime, form.breakMinutes),
        [form.startTime, form.endTime, form.breakMinutes]
    );

    if (!isOpen) return null;

    const setField = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));
    const today = new Date().toISOString().split('T')[0];

    const handleSaveSingle = async () => {
        setError('');
        if (!form.employeeID) { setError('Please select an employee'); return; }
        if (!form.workday)    { setError('Date is required'); return; }
        setSaving(true);
        try {
            if (editData) {
                await api.put(
                    `/api/work-reports/${editData.employeeID}/${editData.workday}/${editData.shift}`,
                    form
                );
            } else {
                await api.post('/api/work-reports', form);
            }
            onSaved(); onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        } finally { setSaving(false); }
    };

    const handleSaveWeek = async () => {
        setError('');
        if (!weekEmpID) { setError('Please select an employee'); return; }
        const activeDays = weekRows.filter(r => r.enabled);
        if (activeDays.length === 0) { setError('Check at least one working day'); return; }
        setSaving(true);
        try {
            for (const row of activeDays) {
                await api.post('/api/work-reports', {
                    employeeID:   weekEmpID,
                    workday:      row.date,
                    shift:        row.shift,
                    startTime:    row.startTime,
                    endTime:      row.endTime,
                    breakMinutes: row.breakMinutes,
                });
            }
            onSaved(); onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        } finally { setSaving(false); }
    };

    const jsonValid = jsonParsed.rows.filter(r => !r.error);
    const jsonBad = jsonParsed.rows.length - jsonValid.length;
    const copy = async (text, what) => {
        try { await navigator.clipboard.writeText(text); setCopied(what); setTimeout(() => setCopied(''), 2000); } catch { /* no clipboard */ }
    };
    const handleSaveJson = async () => {
        setError('');
        if (jsonParsed.errors.length) { setError(jsonParsed.errors[0]); return; }
        if (!jsonValid.length) { setError('No records to add'); return; }
        if (jsonBad) { setError(`${jsonBad} row${jsonBad > 1 ? 's have' : ' has'} problems — fix the JSON or remove them`); return; }
        setSaving(true);
        try {
            for (const r of jsonValid) {
                await api.post('/api/work-reports', {
                    employeeID: r.employeeID, workday: r.workday, shift: r.shift,
                    startTime: r.startTime, endTime: r.endTime, breakMinutes: r.breakMinutes,
                });
            }
            onSaved(); onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        } finally { setSaving(false); }
    };

    const weekLabel = weekAnchor ? (() => {
        const dates = getWeekDates(weekAnchor);
        return `${fmtDate(dates[0])} – ${fmtDate(dates[6])}`;
    })() : '';

    const weekActiveDaysCount = weekRows.filter(r => r.enabled).length;
    const weekTotalHours = weekRows
        .filter(r => r.enabled)
        .reduce((sum, r) => sum + (Number(calcHours(r.startTime, r.endTime, r.breakMinutes)) || 0), 0);

    return (
        <div className="frm-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className={`frm-popup wr-popup${mode !== 'single' ? ' wr-popup--wide' : ''}`}>

                <div className="frm-header">
                    <div>
                        <div className="frm-title">
                            {editData ? 'Edit Work Record' : mode === 'week' ? 'Weekly Work Report' : mode === 'json' ? 'Import from JSON' : 'Add Work Record'}
                        </div>
                        <div className="frm-subtitle">
                            {editData
                                ? `${editData.employeeName} · ${editData.workday}`
                                : mode === 'week'
                                    ? weekLabel ? `Week: ${weekLabel}` : 'Select a week to fill in'
                                    : mode === 'json'
                                        ? 'Paste the JSON an AI prepared from the employees\' messages — any number of days and people at once'
                                        : 'Fill in employee, date and work hours'}
                        </div>
                    </div>
                    <button className="frm-close" onClick={onClose}><i className="fas fa-times" /></button>
                </div>

                {!editData && (
                    <div className="wr-mode-tabs">
                        <button
                            className={`wr-mode-tab${mode === 'single' ? ' wr-mode-tab--active' : ''}`}
                            onClick={() => setMode('single')}>
                            <i className="fas fa-calendar-day" /> Single Day
                        </button>
                        <button
                            className={`wr-mode-tab${mode === 'week' ? ' wr-mode-tab--active' : ''}`}
                            onClick={switchToWeek}>
                            <i className="fas fa-calendar-week" /> Full Week
                        </button>
                        <button
                            className={`wr-mode-tab${mode === 'json' ? ' wr-mode-tab--active' : ''}`}
                            onClick={() => setMode('json')}>
                            <i className="fas fa-code" /> Paste JSON
                        </button>
                    </div>
                )}

                {mode === 'json' ? (
                    <div className="frm-body">
                        <div className="wr-json-help">
                            <span>Copy the prompt, paste it into the AI chat together with the employees' messages, then paste the JSON it returns here.</span>
                            <span className="wr-json-help__btns">
                                <button type="button" className="wr-json-btn" onClick={() => copy(aiPrompt(new Date().getFullYear(), employees.map(e => e.fullName || e.employeeName), shifts.map(s => s.name)), 'prompt')}>
                                    <i className="fas fa-robot" /> {copied === 'prompt' ? 'Copied!' : 'Copy AI prompt'}
                                </button>
                                <button type="button" className="wr-json-btn" onClick={() => copy(EXAMPLE_JSON, 'example')}>
                                    <i className="fas fa-file-code" /> {copied === 'example' ? 'Copied!' : 'Copy example JSON'}
                                </button>
                            </span>
                        </div>
                        <textarea className="wr-input wr-json" rows={7} value={jsonText} onChange={e => setJsonText(e.target.value)} spellCheck={false}
                            placeholder={EXAMPLE_JSON} />
                        {jsonParsed.errors.map((e, i) => <div key={i} className="frm-error"><i className="fas fa-exclamation-triangle" /> {e}</div>)}
                        {jsonParsed.rows.length > 0 && (
                            <div className="wr-json-preview">
                                <table className="wr-json-table">
                                    <thead><tr><th>#</th><th>Employee</th><th>Date</th><th>Shift</th><th>Start</th><th>End</th><th>Break</th><th>Hours</th><th>Status</th></tr></thead>
                                    <tbody>
                                        {jsonParsed.rows.map(r => (
                                            <tr key={r.n} className={r.error ? 'wr-json-row--err' : ''}>
                                                <td>{r.n}</td><td>{r.employeeName}</td><td>{r.workday || '—'}</td><td>{r.shiftName || '—'}</td>
                                                <td>{r.startTime || '—'}</td><td>{r.endTime || '—'}</td><td>{r.breakMinutes ? `${r.breakMinutes}m` : '—'}</td>
                                                <td>{r.hours != null ? `${r.hours}h` : '—'}</td>
                                                <td>{r.error ? <span className="wr-json-err"><i className="fas fa-times-circle" /> {r.error}</span> : <span className="wr-json-ok"><i className="fas fa-check-circle" /> ok</span>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="wr-json-summary">
                                    {jsonValid.length} record{jsonValid.length !== 1 ? 's' : ''} · {jsonValid.reduce((s, r) => s + (r.hours || 0), 0).toFixed(2)}h
                                    {jsonBad > 0 && <span className="wr-json-err"> · {jsonBad} with problems</span>}
                                    <span className="wr-json-note"> · existing records for the same employee, day and shift are overwritten</span>
                                </div>
                            </div>
                        )}
                        {error && <div className="frm-error"><i className="fas fa-exclamation-triangle" /> {error}</div>}
                    </div>
                ) : mode === 'single' ? (
                    <div className="frm-body">
                        <div className="wr-grid">
                            <div className="wr-field">
                                <label className="wr-label">Employee *</label>
                                <select className="wr-input" value={form.employeeID}
                                    onChange={setField('employeeID')}
                                    disabled={!!editData}>
                                    <option value="">— Select employee —</option>
                                    {employees.map(e => (
                                        <option key={e.employeeID} value={e.employeeID}>{e.fullName}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="wr-field">
                                <label className="wr-label">Date *</label>
                                <input className="wr-input" type="date" value={form.workday}
                                    onChange={setField('workday')}
                                    max={today}
                                    disabled={!!editData} />
                            </div>
                        </div>

                        <div className="wr-field wr-field--sm">
                            <label className="wr-label">Shift</label>
                            <div className="wr-shift-row">
                                {shifts.map(s => (
                                    <button key={s.id} type="button"
                                        className={`wr-shift-btn${form.shift === String(s.id) ? ' wr-shift-btn--active' : ''}`}
                                        onClick={() => setForm(p => ({ ...p, shift: String(s.id) }))}>
                                        {s.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="wr-grid">
                            <div className="wr-field">
                                <label className="wr-label">Start Time</label>
                                <input className="wr-input wr-time" type="time" value={form.startTime}
                                    onChange={setField('startTime')} />
                            </div>
                            <div className="wr-field">
                                <label className="wr-label">End Time</label>
                                <input className="wr-input wr-time" type="time" value={form.endTime}
                                    onChange={setField('endTime')} />
                            </div>
                        </div>

                        <div className="wr-field wr-field--sm">
                            <label className="wr-label"><i className="fas fa-coffee wr-icon" /> Break (minutes)</label>
                            <div className="wr-break-row">
                                {[0,15,30,45,60].map(m => (
                                    <button key={m} type="button"
                                        className={`wr-break-btn${form.breakMinutes === String(m) ? ' wr-break-btn--active' : ''}`}
                                        onClick={() => setForm(p => ({ ...p, breakMinutes: String(m) }))}>
                                        {m === 0 ? 'None' : `${m}m`}
                                    </button>
                                ))}
                                <input className="wr-input wr-break-custom" type="number"
                                    min="0" max="120" step="5"
                                    value={form.breakMinutes}
                                    onChange={setField('breakMinutes')}
                                    title="Custom break in minutes" />
                            </div>
                        </div>

                        <div className={`wr-hours-summary${hours ? ' wr-hours-summary--filled' : ''}`}>
                            <i className="fas fa-business-time" />
                            {hours
                                ? <><span className="wr-hours-num">{hours}h</span> worked this shift</>
                                : <span className="wr-hours-empty">Hours will appear when start & end times are set</span>
                            }
                        </div>

                        {error && <div className="frm-error"><i className="fas fa-exclamation-triangle" /> {error}</div>}
                    </div>

                ) : (

                    <div className="frm-body">
                        {/* Employee + week picker */}
                        <div className="wr-grid">
                            <div className="wr-field">
                                <label className="wr-label">Employee *</label>
                                <select className="wr-input" value={weekEmpID}
                                    onChange={e => setWeekEmpID(e.target.value)}>
                                    <option value="">— Select employee —</option>
                                    {employees.map(e => (
                                        <option key={e.employeeID} value={e.employeeID}>{e.fullName}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="wr-field">
                                <label className="wr-label">Any date in the week</label>
                                <input className="wr-input" type="date" value={weekAnchor}
                                    max={today}
                                    onChange={handleWeekAnchorChange} />
                            </div>
                        </div>

                        {/* Quick fill */}
                        {weekRows.length > 0 && (
                            <div className="wr-quickfill">
                                <span className="wr-qf-label">Quick fill:</span>
                                <div className="wr-qf-shifts">
                                    {shifts.map(s => (
                                        <button key={s.id} type="button"
                                            className={`wr-shift-btn wr-shift-btn--xs${quickFill.shift === String(s.id) ? ' wr-shift-btn--active' : ''}`}
                                            onClick={() => setQuickFill(p => ({ ...p, shift: String(s.id) }))}>
                                            {s.name}
                                        </button>
                                    ))}
                                </div>
                                <input className="wr-input wr-time wr-qf-time" type="time"
                                    value={quickFill.startTime}
                                    onChange={e => setQuickFill(p => ({ ...p, startTime: e.target.value }))} />
                                <input className="wr-input wr-time wr-qf-time" type="time"
                                    value={quickFill.endTime}
                                    onChange={e => setQuickFill(p => ({ ...p, endTime: e.target.value }))} />
                                <input className="wr-input wr-qf-brk" type="number"
                                    min="0" max="120" step="5" title="Break (min)"
                                    value={quickFill.breakMinutes}
                                    onChange={e => setQuickFill(p => ({ ...p, breakMinutes: e.target.value }))} />
                                <button type="button" className="wr-qf-apply" onClick={applyQuickFill}>
                                    <i className="fas fa-fill-drip" /> Apply to active days
                                </button>
                            </div>
                        )}

                        {/* 7-day table */}
                        {weekRows.length > 0 && (
                            <div className="wr-week-table">
                                <div className="wr-week-head">
                                    <span />
                                    <span>Day</span>
                                    <span>Date</span>
                                    <span>Shift</span>
                                    <span>Start</span>
                                    <span>End</span>
                                    <span title="Break (min)">Brk</span>
                                    <span>Hours</span>
                                </div>

                                {weekRows.map((row, idx) => {
                                    const h = row.enabled
                                        ? calcHours(row.startTime, row.endTime, row.breakMinutes)
                                        : null;
                                    return (
                                        <div key={row.date}
                                            className={`wr-week-row${!row.enabled ? ' wr-week-row--off' : ''}${row.isFuture ? ' wr-week-row--future' : ''}`}>
                                            <input type="checkbox" className="wr-week-chk"
                                                checked={row.enabled}
                                                disabled={row.isFuture}
                                                onChange={e => updateWeekRow(idx, 'enabled', e.target.checked)} />
                                            <span className="wr-week-dn">{row.dayName}</span>
                                            <span className="wr-week-dt">{fmtDate(row.date)}</span>
                                            <div className="wr-week-shifts">
                                                {shifts.map(s => (
                                                    <button key={s.id} type="button"
                                                        disabled={!row.enabled}
                                                        className={`wr-shift-btn wr-shift-btn--xs${row.shift === String(s.id) ? ' wr-shift-btn--active' : ''}`}
                                                        onClick={() => updateWeekRow(idx, 'shift', String(s.id))}>{s.name}</button>
                                                ))}
                                            </div>
                                            <input className="wr-input wr-time" type="time"
                                                disabled={!row.enabled}
                                                value={row.startTime}
                                                onChange={e => updateWeekRow(idx, 'startTime', e.target.value)} />
                                            <input className="wr-input wr-time" type="time"
                                                disabled={!row.enabled}
                                                value={row.endTime}
                                                onChange={e => updateWeekRow(idx, 'endTime', e.target.value)} />
                                            <input className="wr-input wr-week-brk" type="number"
                                                min="0" max="120" step="5"
                                                disabled={!row.enabled}
                                                value={row.breakMinutes}
                                                onChange={e => updateWeekRow(idx, 'breakMinutes', e.target.value)} />
                                            <span className={`wr-week-hrs${h ? ' wr-week-hrs--ok' : ''}`}>
                                                {h ? `${h}h` : '—'}
                                            </span>
                                        </div>
                                    );
                                })}

                                {weekActiveDaysCount > 0 && (
                                    <div className="wr-week-total">
                                        <span>{weekActiveDaysCount} day{weekActiveDaysCount !== 1 ? 's' : ''}</span>
                                        <span className="wr-week-total-h">{weekTotalHours.toFixed(2)}h total</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {error && <div className="frm-error"><i className="fas fa-exclamation-triangle" /> {error}</div>}
                    </div>
                )}

                <div className="frm-footer">
                    <button className="frm-btn frm-btn--ghost" onClick={onClose}>Cancel</button>
                    <button className="frm-btn frm-btn--primary"
                        onClick={mode === 'week' ? handleSaveWeek : mode === 'json' ? handleSaveJson : handleSaveSingle}
                        disabled={saving || (mode === 'json' && !jsonValid.length)}>
                        {saving
                            ? <><i className="fas fa-spinner fa-spin" /> Saving…</>
                            : mode === 'week'
                                ? <><i className="fas fa-check" /> Save {weekActiveDaysCount} Day{weekActiveDaysCount !== 1 ? 's' : ''}</>
                                : mode === 'json'
                                    ? <><i className="fas fa-check" /> Add {jsonValid.length} Record{jsonValid.length !== 1 ? 's' : ''}</>
                                    : <><i className="fas fa-check" /> {editData ? 'Save Changes' : 'Add Record'}</>
                        }
                    </button>
                </div>

            </div>
        </div>
    );
};

export default WorkReportPopup;
